"use client"

import { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { X, Lock, StickyNote, Users, Save, PenSquare, Calendar, Hash, Type, ToggleLeft, List, Mail, CheckSquare, Pin, Plus, Trash2, GripVertical } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import { useLanguage } from "@/context/language-context"
import { useLocalizedField } from "@/utils/language-utils"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useToast } from "@/hooks/use-toast"
import { useViewManager } from "../view-manager-context"
import { mapCustomFieldInputTypeToColumnType, resolveFieldInputType } from "@/utils/column-type-helpers"
import { ColorPicker } from "@/components/color-picker"

// ─── Sortable Dropdown Option ────────────────────────────────────────

function SortableOption({ id, children, disabled }: { id: string; children: React.ReactNode; disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-2"
    >
      {!disabled && (
        <div {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600">
          <GripVertical size={14} />
        </div>
      )}
      {children}
    </div>
  )
}

// ─── Field Edit Panel ────────────────────────────────────────────────

// Module-level ref to track dirty state — accessible from parent components
export const fieldEditDirtyRef = { current: false }

interface FieldEditPanelProps {
  canEdit: boolean
  addMode?: boolean
}

export function FieldEditPanel({ canEdit, addMode = false }: FieldEditPanelProps) {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const { state, dispatch } = useViewManager()
  const { configuration } = useWorkflow()
  const { updateFieldConfiguration, createCustomFieldConfiguration, searchUsers } = useWorkflowApi()
  const { toast } = useToast()
  const getLocalizedField = useLocalizedField()

  // Add mode — new blank field
  if (addMode) {
    return (
      <FieldEditPanelInner
        key="__new__"
        field={null}
        fieldId="__new__"
        canEdit={true}
        addMode={true}
      />
    )
  }

  const fieldId = state.selectedFieldId
  if (!fieldId) return null

  // Find the field config — try view designer's state first, fall back to global configuration
  const fieldsInUse = state.originalConfig?.fieldsInUse || configuration?.fieldsInUse || []
  const stageConfig = (state.originalConfig?.stageConfigurations || []).find(
    (s: any) => (s.stageId || s.stageCode) === state.activeEditStage
  )
  const stageFields = stageConfig?.fieldConfigurations || []

  // Merge stage-level field with fieldsInUse metadata (isEnabledForClients, isPrivate, etc.)
  const stageField = stageFields.find((f: any) => f.fieldConfigurationId === fieldId)
  const masterField = fieldsInUse.find((f: any) => f.fieldConfigurationId === fieldId)
  const field = stageField
    ? (masterField ? { ...masterField, ...stageField } : stageField)
    : masterField

  if (!field) return null

  // Gate edit affordances when the field is sourced from the shared Default
  // partition. Editing a Default-tenant field mutates the cross-tenant schema
  // (every tenant sees the same column metadata for fields they don't override
  // per-tenant), so the default is read-only inside a single tenant's view
  // manager. Users with the EditDefaultSettings permission — typically platform
  // admins or cross-tenant schema owners — can opt in to editing Default rows.
  // Tenant-scoped fields fall through to the existing EditColumns permission
  // (already encoded in the canEdit prop).
  const authorizedActions = (configuration?.authorizedActions || [])
    .map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const canEditDefaultSettings = authorizedActions.includes('EditDefaultSettings')
  const isDefaultTenantField =
    typeof field?.tenantName === 'string' &&
    field.tenantName.toLowerCase() === 'default'
  const effectiveCanEdit = canEdit && (!isDefaultTenantField || canEditDefaultSettings)

  return (
    <FieldEditPanelInner
      key={fieldId}
      field={field}
      fieldId={fieldId}
      canEdit={effectiveCanEdit}
    />
  )
}

// Inner component with its own state (keyed by fieldId to reset on field change)
// Input types the designer can create, and their labels. Kept next to each other so a new type can't
// be added to one without the other. Availability is filtered against free slots at render time.
const INPUT_TYPE_OPTIONS = ['Text', 'Dropdown', 'Date', 'Number', 'YesNo', 'Email', 'Checklist'] as const
// Translation keys rather than literals: the label beside this control localises, so hardcoding the
// values showed a French user "Type d'entrée: Yes/No". The Edit-columns modal already resolves these
// through t() (getInputTypeLabel) — same parity problem this file is fixing, one layer up.
const INPUT_TYPE_LABEL_KEYS: Record<string, TranslationKey> = {
  Text: 'inputTypeText',
  Dropdown: 'inputTypeDropdown',
  Date: 'inputTypeDate',
  Number: 'inputTypeNumber',
  YesNo: 'inputTypeYesNo',
  Email: 'inputTypeEmail',
  Checklist: 'inputTypeChecklist',
}

function FieldEditPanelInner({ field, fieldId, canEdit, addMode = false }: { field: any; fieldId: string; canEdit: boolean; addMode?: boolean }) {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const { state, dispatch } = useViewManager()
  const { configuration, refreshConfiguration } = useWorkflow()
  const { updateFieldConfiguration, createCustomFieldConfiguration, searchUsers } = useWorkflowApi()
  const { toast } = useToast()
  const getLocalizedField = useLocalizedField()

  const isCustomField = addMode ? true : (field?.isCustomField || field?.mappingSource === 'FboCustomField')

  const inputTypeLabel = (type: string) =>
    INPUT_TYPE_LABEL_KEYS[type] ? t(INPUT_TYPE_LABEL_KEYS[type]) : type

  // Permissions
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const canViewPrivate = authorizedActions.includes("ViewPrivateColumns")
  const canDeleteCustom = authorizedActions.includes("DeleteCustomColumns")

  // ─── Local form state (initialized from field, or empty for addMode) ─
  const [fieldName, setFieldName] = useState(field ? (getLocalizedField(field, 'columnName') || field.columnCode || '') : '')
  const [inputType, setInputType] = useState(field?.customFieldInputTypeCode || 'Text')
  const [isPrivate, setIsPrivate] = useState(field ? !!field.isPrivate : false)
  const [authorizedUsers, setAuthorizedUsers] = useState<any[]>(field?.authorizedUserList || [])
  const [displayNote, setDisplayNote] = useState(field ? !!field.displayNote : false)
  const [note, setNote] = useState(field?.note || '')
  const [isEnabledForClients, setIsEnabledForClients] = useState(field ? !!field.isEnabledForClients : false)
  // Defaults for a NEW field match the Edit-columns modal, which defaults retain-data ON — the two
  // surfaces previously produced fields with different retention behaviour depending on where you
  // created them.
  const [isRetainDataEnabled, setIsRetainDataEnabled] = useState(field ? !!field.isRetainDataEnabled : true)
  const [fieldFormatting, setFieldFormatting] = useState(field?.fieldFormatting || 'ShortDate')
  const [isMultiSelect, setIsMultiSelect] = useState(field ? !!field.allowMultipleSelections : false)
  const [useColorCode, setUseColorCode] = useState(field?.customValues?.some((v: any) => v.color) || false)
  const [dropdownOptions, setDropdownOptions] = useState<{ value: string; color: string; code?: string | null }[]>(
    (field?.customValues || []).filter((v: any) => !v.isDeleted).map((v: any) => ({
      value: getLocalizedField(v, 'title') || v.code || '',
      color: v.color || '',
      code: v.code,
    }))
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  // Color picker state for dropdown option swatches
  const [activeColorOption, setActiveColorOption] = useState<number | null>(null)
  const [showColorPicker, setShowColorPicker] = useState(false)

  // Derived values — react to inputType state for addMode
  // Yes/No fields have exactly two FIXED values. They must never be treated as a dropdown here:
  // the dropdown editor allows renaming + add/remove, which is invalid for a boolean. They are
  // recolor-only (see the Yes/No block below). Without this, `hasCustomValues` made every YesNo
  // field fall into the dropdown branch (it does carry its Yes/No customValues).
  // Resolve the input type the SAME way the Edit-columns modal does. Reading
  // `customFieldInputTypeCode` on its own was the bug: older custom fields carry a null code, so a
  // Boolean column fell through to the dropdown branch via `hasCustomValues` and rendered the full
  // options editor — rename, reorder, add, delete — on a field that has exactly two fixed values.
  // That is how boolean fields in prod ended up with renamed and extra options. resolveFieldInputType
  // falls back to the storage type, which also stops numeric/date/email columns being offered a
  // dropdown editor here, matching the modal.
  const effectiveInputType = addMode ? inputType : resolveFieldInputType(field)
  const isYesNo = effectiveInputType === 'YesNo'
  const isDropdown = addMode
    ? inputType === 'Dropdown'
    : isCustomField && effectiveInputType === 'Dropdown'
  const isDateField = addMode
    ? inputType === 'Date'
    : (field?.columnTypeCode === 'Date' || field?.columnTypeCode === 'DateTime')

  // ─── Slot availability (parity with the Edit-columns modal) ───
  // Custom fields occupy a finite pool of physical slots per column type. Without these guards the
  // designer happily offered an input type whose pool was exhausted and the create failed server-side
  // with "No available custom fields of type 'X'". customFieldsMetadata lists the FREE slots by type.
  const availableColumnTypes: string[] = (configuration?.customFieldsMetadata || []).map((m: any) => m.inputDataType)
  const availableInputTypes = INPUT_TYPE_OPTIONS.filter((type) => {
    if (type === 'Dropdown') {
      // Dropdown can land in Text (single) or TextList (multi) — offer it if either is free.
      return availableColumnTypes.includes(mapCustomFieldInputTypeToColumnType(type, false))
        || availableColumnTypes.includes(mapCustomFieldInputTypeToColumnType(type, true))
    }
    return availableColumnTypes.includes(mapCustomFieldInputTypeToColumnType(type, isMultiSelect))
  })
  // Only blocks CREATE. Editing an existing field must stay possible even when the pool is full.
  const noCustomFieldsAvailable = addMode && availableInputTypes.length === 0
  // Multi-select turns a Dropdown into a TextList slot, so it needs one to be free. Resolved through
  // the shared mapper rather than a hardcoded 'TextList' so it can't drift from the mapping the
  // availability check above already uses.
  const disableMultiSelect = inputType === 'Dropdown'
    && !availableColumnTypes.includes(mapCustomFieldInputTypeToColumnType('Dropdown', true))

  // Keep the selection inside the offered set. `inputType` initializes to 'Text' and nothing else
  // re-syncs it, so when Text has no free slot but (say) Date does, the <select value="Text"> matches no
  // <option>: the browser paints the first one (Date) WITHOUT firing onChange, so state still says Text
  // and Add posts customFieldInputTypeCode: 'Text' → the exact "No available custom fields of type
  // 'Text'" server error the slot guards above exist to prevent. noCustomFieldsAvailable can't catch it
  // because the list is non-empty.
  useEffect(() => {
    if (!addMode || availableInputTypes.length === 0) return
    if (!availableInputTypes.includes(inputType)) {
      setInputType(availableInputTypes[0])
    }
  }, [addMode, inputType, availableInputTypes.join(',')])

  // User search state for private columns
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<any[]>([])
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastSearchRef = useRef('')
  const searchUsersRef = useRef(searchUsers)
  searchUsersRef.current = searchUsers

  // Mark dirty on any change — also sync the module-level ref
  const markDirty = () => { setIsDirty(true); fieldEditDirtyRef.current = true }

  // Reset the ref when this component mounts/unmounts
  useEffect(() => {
    fieldEditDirtyRef.current = false
    return () => { fieldEditDirtyRef.current = false }
  }, [])

  // Stage visibility from the view designer's draft
  const stages = state.originalConfig?.stageConfigurations || []
  const getStageVisibility = (stageId: string) => (state.draftStageColumns[stageId] || []).includes(fieldId)
  const toggleStageVisibility = (stageId: string) => {
    dispatch({ type: 'TOGGLE_COLUMN', payload: { stageId, fieldId } })
    markDirty()
  }

  // Stable ref for authorizedUsers to avoid stale closure in debounced search
  const authorizedUsersRef = useRef(authorizedUsers)
  authorizedUsersRef.current = authorizedUsers

  // User search debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    if (userSearchQuery.length < 2) { setUserSearchResults([]); return }
    searchTimeoutRef.current = setTimeout(async () => {
      if (userSearchQuery === lastSearchRef.current) return
      lastSearchRef.current = userSearchQuery
      setIsSearchingUsers(true)
      try {
        const results = await searchUsersRef.current(userSearchQuery)
        const existingIds = authorizedUsersRef.current.map((u: any) => u.profileId)
        setUserSearchResults(results.filter((r: any) => !existingIds.includes(r.profileId)))
      } catch { setUserSearchResults([]) }
      finally { setIsSearchingUsers(false) }
    }, 800)
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current) }
  }, [userSearchQuery])

  // DnD sensors for dropdown options
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = dropdownOptions.findIndex((_, i) => `opt-${i}` === active.id)
    const newIdx = dropdownOptions.findIndex((_, i) => `opt-${i}` === over.id)
    if (oldIdx !== -1 && newIdx !== -1) {
      setDropdownOptions(arrayMove(dropdownOptions, oldIdx, newIdx))
      markDirty()
    }
  }

  // ─── Save handler ──────────────────────────────────────────────────
  const handleSave = async () => {
    // Name is required on BOTH paths — an edit that blanks the name used to save an unnamed column.
    if (!fieldName.trim()) {
      toast({ variant: "destructive", title: t('error') || "Error", description: t('fieldNameRequired') || "Field name is required" })
      return
    }

    // A private column with nobody authorised is invisible to everyone including its author (parity
    // with the Edit-columns modal, which blocks this).
    if (isPrivate && authorizedUsers.length === 0) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: t('privateColumnNeedsUser') || "Select at least one authorized user for a private column.",
      })
      return
    }

    if (addMode && noCustomFieldsAvailable) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: t('noCustomFieldsAvailable') || "No custom field slots are available for this tenant.",
      })
      return
    }

    setIsSaving(true)
    try {
      if (addMode) {
        // ── CREATE new custom field ──
        const customValues = (inputType === 'Dropdown')
          ? dropdownOptions.map(opt => ({
              code: null,
              titleEnglish: opt.value,
              titleFrench: opt.value,
              color: opt.color || '',
              isDeleted: false,
            }))
          : []

        await createCustomFieldConfiguration({
          columnNameEnglish: fieldName,
          columnNameFrench: fieldName,
          color: null,
          customValues,
          customFieldInputTypeCode: inputType,
          allowMultipleSelections: isMultiSelect,
          isPrivate: Boolean(isPrivate),
          authorizedUserList: isPrivate ? authorizedUsers : [],
          note: note || null,
          displayNote: Boolean(displayNote && note?.trim()),
          isRetainDataEnabled: Boolean(isRetainDataEnabled),
          isEnabledForClients: Boolean(isEnabledForClients),
          fieldFormatting: inputType === 'Date' ? fieldFormatting : null,
          // Attach the new field to every stage of the current entity. Sending [] left it attached to
          // nothing, so a freshly created custom field was invisible until the user added it manually
          // from the Columns step. Pre-WO entities have a single "Requests" stage, so this is exactly
          // right there; for WorkOrder the user can still untoggle per stage afterwards.
          // `stages` comes from state.originalConfig, which only LOAD_VIEW populates — so it is [] when
          // the Custom Fields step was entered straight from the views list (no view selected), which is
          // precisely when view-manager renders that button. Fall back to the live configuration, which
          // carries the same stageConfigurations shape, or the attach-to-nothing bug survives for that
          // entry point.
          stageIds: (state.originalConfig?.stageConfigurations || configuration?.stageConfigurations || [])
            .map((s: any) => s.stageId || s.stageCode)
            .filter(Boolean),
          currentViewSettingsId: state.selectedView?.viewSettingsId ?? null,
        })

        // Refresh configuration to pick up the new field
        await refreshConfiguration(true)

        setIsDirty(false)
        fieldEditDirtyRef.current = false
        dispatch({ type: 'SET_ADDING_CUSTOM_FIELD', payload: false })
        toast({ variant: "success", title: t('success') || "Success", description: t('fieldCreated') || "Custom field created" })
        return
      }

      // ── UPDATE existing field ──
      // Stage membership. The backend treats stageIds as the COMPLETE desired set: any stage not in
      // the list has the field REMOVED. Sending [] (as this panel used to) therefore deleted the
      // column from every stage + from the caller's UserViewState — editing a colour silently
      // dropped the column. So:
      //   - a view is selected -> send the real set derived from the designer's draft toggles
      //   - no view selected (opened from Manage Custom Fields, no stage UI shown) -> send null,
      //     which the backend interprets as "leave stage membership untouched".
      const stageIds: string[] | null = state.selectedView
        ? stages
            .map((s: any) => s.stageId || s.stageCode)
            .filter((id: string) => (state.draftStageColumns[id] || []).includes(fieldId))
        : null

      // Build custom values. Yes/No is recolor-only: keep the ORIGINAL titles (they're fixed and
      // not user-editable) and persist just the colors, so a save can't rename/add/remove values.
      const customValues = (isDropdown || isYesNo)
        ? dropdownOptions.map(opt => {
            const original = field?.customValues?.find((v: any) => v.code === opt.code)
            if (isYesNo) {
              return {
                code: opt.code || null,
                titleEnglish: original?.titleEnglish ?? opt.value,
                titleFrench: original?.titleFrench ?? opt.value,
                color: opt.color || '',
                isDeleted: false,
              }
            }
            return {
              code: opt.code || null,
              titleEnglish: language === 'en' ? opt.value : (original?.titleEnglish || opt.value),
              titleFrench: language === 'fr' ? opt.value : (original?.titleFrench || opt.value),
              color: opt.color || '',
              isDeleted: false,
            }
          })
        : []

      const fieldConfig = {
        ...(field || {}),
        columnNameEnglish: language === 'en' ? fieldName : (field?.columnNameEnglish || fieldName),
        columnNameFrench: language === 'fr' ? fieldName : (field?.columnNameFrench || fieldName),
        isPrivate: Boolean(isPrivate),
        authorizedUserList: isPrivate ? authorizedUsers : [],
        note: note || null,
        displayNote: Boolean(displayNote && note?.trim()),
        isEnabledForClients: Boolean(isEnabledForClients),
        isRetainDataEnabled: Boolean(isRetainDataEnabled),
        fieldFormatting: isDateField ? fieldFormatting : null,
        allowMultipleSelections: isDropdown ? isMultiSelect : false,
        customValues: (isDropdown || isYesNo) ? customValues : (field?.customValues || []),
      }

      // Pass the active view so its own per-stage column list is updated in the same call (matches
      // the Edit-columns modal). Null when no view is selected.
      await updateFieldConfiguration(fieldId, fieldConfig, stageIds, state.selectedView?.viewSettingsId ?? null)

      // Update the view designer's field metadata locally (no heavy refresh)
      const fieldsSource = state.originalConfig?.fieldsInUse || configuration?.fieldsInUse || []
      const updatedFieldsInUse = fieldsSource.map((f: any) =>
        f.fieldConfigurationId === fieldId ? { ...f, ...fieldConfig } : f
      )
      const stageSource = state.originalConfig?.stageConfigurations || configuration?.stageConfigurations || []
      const updatedStageConfigs = stageSource.map((stage: any) => ({
        ...stage,
        fieldConfigurations: (stage.fieldConfigurations || []).map((f: any) =>
          f.fieldConfigurationId === fieldId ? { ...f, ...fieldConfig } : f
        ),
      }))

      dispatch({
        type: 'UPDATE_FIELD_METADATA',
        payload: { fieldsInUse: updatedFieldsInUse, stageConfigurations: updatedStageConfigs },
      })

      setIsDirty(false)
      fieldEditDirtyRef.current = false
      toast({ variant: "success", title: t('success') || "Success", description: t('fieldUpdated') || "Field updated" })
    } catch (err) {
      toast({ variant: "destructive", title: t('error') || "Error", description: err instanceof Error ? err.message : "Failed to save field" })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="w-96 border-l-2 border-blue-200 bg-gradient-to-b from-[#F8F8FF] to-white flex flex-col h-full flex-shrink-0 shadow-lg">
      {/* Header — fixed height matching custom-fields header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-[#F0EEFF]">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-[#0F214B] truncate">{addMode ? (t('addNewField') || 'New Custom Field') : fieldName}</h3>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {addMode ? 'Create a new custom field' : canEdit ? 'Edit Field Properties' : 'Field Properties (view only)'}
          </p>
        </div>
        <button
          onClick={() => {
            if (isDirty) {
              const confirmed = window.confirm('You have unsaved field changes. Discard them?')
              if (!confirmed) return
              fieldEditDirtyRef.current = false
            }
            if (addMode) {
              dispatch({ type: 'SET_ADDING_CUSTOM_FIELD', payload: false })
            } else {
              dispatch({ type: 'SELECT_FIELD', payload: null })
            }
          }}
          className="p-1 rounded hover:bg-gray-100 flex-shrink-0"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Basic Info */}
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">{t('fieldName') || 'Field Name'}</Label>
            {(canEdit && isCustomField) || addMode ? (
              <Input value={fieldName} onChange={(e) => { setFieldName(e.target.value); markDirty() }} className="h-8 text-sm mt-1" placeholder="Enter field name..." />
            ) : (
              <p className="text-sm text-gray-700 font-medium mt-0.5">{fieldName}</p>
            )}
          </div>
          {addMode ? (
            <div>
              <Label className="text-[10px] text-gray-400 uppercase tracking-wide">{t('inputType') || 'Input Type'}</Label>
              {/* Only offer types that still have a free slot — creating one without a slot fails
                  server-side with "No available custom fields of type 'X'". */}
              <select
                value={inputType}
                onChange={(e) => { setInputType(e.target.value); markDirty() }}
                disabled={noCustomFieldsAvailable}
                className="w-full h-8 text-sm mt-1 border border-gray-200 rounded px-2 bg-white disabled:bg-gray-50 disabled:text-gray-400"
              >
                {availableInputTypes.map((type) => (
                  <option key={type} value={type}>{inputTypeLabel(type)}</option>
                ))}
              </select>
              {noCustomFieldsAvailable && (
                <p className="text-xs text-amber-600 mt-1">
                  {t('noCustomFieldsAvailable') || 'No custom field slots are available for this tenant. Free one up by deleting an unused custom field.'}
                </p>
              )}
            </div>
          ) : (
            <>
              {field && (
                <div>
                  <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Column Code</Label>
                  <p className="text-sm text-gray-700 font-mono bg-gray-50 px-2 py-1 rounded mt-0.5">{field.columnCode}</p>
                </div>
              )}
              {/* Custom fields show the INPUT type in the same control the Edit-columns modal uses,
                  disabled for the same reason it is there: the input type fixes which physical slot
                  family the value lives in, so it cannot change once the field exists. System columns
                  have no input type — they keep showing their storage type. */}
              {isCustomField ? (
                <div>
                  <Label className="text-[10px] text-gray-400 uppercase tracking-wide">{t('inputType') || 'Input type'}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <select
                      value={effectiveInputType}
                      disabled
                      aria-label={t('inputType') || 'Input type'}
                      className="flex-1 h-8 text-sm border border-gray-200 rounded px-2 bg-gray-50 text-gray-500 disabled:cursor-not-allowed"
                    >
                      <option value={effectiveInputType}>{inputTypeLabel(effectiveInputType)}</option>
                    </select>
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded font-medium flex-shrink-0">Custom</span>
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Type</Label>
                  <p className="text-sm text-gray-700 mt-0.5">{field?.columnTypeCode}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="h-px bg-gray-100" />

        {/* Date Formatting */}
        {isDateField && canEdit && (
          <>
            <div>
              <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-2 block">{t('dateFormatting') || 'Date Format'}</Label>
              <div className="space-y-1">
                {['ShortDate', 'DateTime'].map(fmt => (
                  <label key={fmt} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="radio" name="dateFormat" checked={fieldFormatting === fmt} onChange={() => { setFieldFormatting(fmt); markDirty() }} />
                    {fmt === 'ShortDate' ? (t('shortDateFormat') || 'Short Date') : (t('dateTimeFormat') || 'Date & Time')}
                  </label>
                ))}
              </div>
            </div>
            <div className="h-px bg-gray-100" />
          </>
        )}

        {/* Dropdown Options */}
        {isDropdown && (
          <>
            <div>
              <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-2 block">{t('dropdownOptions') || 'Dropdown Options'}</Label>
              {canEdit && (
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox checked={useColorCode} onCheckedChange={(c) => { setUseColorCode(!!c); markDirty() }} />
                    {t('colorCodeInput') || 'Color code'}
                  </label>
                  <label
                    className={`flex items-center gap-1.5 text-xs ${(!addMode || disableMultiSelect) ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    title={!addMode
                      ? 'Cannot change after field is created'
                      : disableMultiSelect ? 'No TextList slot available for a multi-select dropdown' : undefined}
                  >
                    <Checkbox
                      checked={isMultiSelect}
                      // Multi-select stores into a TextList slot — block it when none is free.
                      disabled={!addMode || disableMultiSelect}
                      onCheckedChange={(c) => { setIsMultiSelect(!!c); markDirty() }}
                    />
                    {t('allowMultipleSelections') || 'Allow multiple'}
                  </label>
                </div>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={canEdit ? handleDragEnd : undefined}>
                <SortableContext items={dropdownOptions.map((_, i) => `opt-${i}`)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1">
                    {dropdownOptions.map((opt, idx) => (
                      <SortableOption key={`opt-${idx}`} id={`opt-${idx}`} disabled={!canEdit}>
                        <Input
                          value={opt.value}
                          onChange={(e) => { if (!canEdit) return; const newOpts = [...dropdownOptions]; newOpts[idx] = { ...opt, value: e.target.value }; setDropdownOptions(newOpts); markDirty() }}
                          className="h-7 text-xs flex-1"
                          disabled={!canEdit}
                        />
                        {(useColorCode || opt.color) && (
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={() => {
                              if (!canEdit) return
                              setActiveColorOption(idx)
                              setShowColorPicker(true)
                            }}
                            className="w-7 h-7 rounded border flex-shrink-0 cursor-pointer disabled:cursor-default"
                            style={{ background: opt.color || '#f0f0f0' }}
                            aria-label={t('colorCodeInput') || 'Change color'}
                          />
                        )}
                        {canEdit && canDeleteCustom && (
                          <button onClick={() => { setDropdownOptions(dropdownOptions.filter((_, i) => i !== idx)); markDirty() }} className="p-1 text-gray-400 hover:text-red-500">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </SortableOption>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {canEdit && (
                <button
                  onClick={() => { setDropdownOptions([...dropdownOptions, { value: '', color: '', code: null }]); markDirty() }}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2"
                >
                  <Plus size={12} /> {t('option') || 'Option'}
                </button>
              )}
            </div>
            <div className="h-px bg-gray-100" />
          </>
        )}

        {/* Yes/No values — RECOLOR ONLY. The two values are fixed: no rename, no add, no remove,
            no multi-select. Only the swatch is interactive. */}
        {isYesNo && !addMode && dropdownOptions.length > 0 && (
          <>
            <div>
              <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-2 block">
                {t('dropdownOptions') || 'Options'}
              </Label>
              <div className="space-y-1">
                {dropdownOptions.map((opt, idx) => (
                  <div key={`yesno-${idx}`} className="flex items-center gap-2">
                    <span className="h-7 flex-1 flex items-center px-2 text-xs text-gray-700 bg-gray-50 border rounded">
                      {opt.value}
                    </span>
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => {
                        if (!canEdit) return
                        setActiveColorOption(idx)
                        setShowColorPicker(true)
                      }}
                      className="w-7 h-7 rounded border flex-shrink-0 cursor-pointer disabled:cursor-default"
                      style={{ background: opt.color || '#f0f0f0' }}
                      aria-label={t('colorCodeInput') || 'Change color'}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="h-px bg-gray-100" />
          </>
        )}

        {/* Stage Visibility — only shown when a view is selected (not from main custom fields flow) */}
        {state.selectedView && (
          <>
            <div>
              <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-2 block">{t('visibleOnStages') || 'Visible on Stages'}</Label>
              <div className="space-y-1">
                {stages.map((stage: any) => {
                  const stageId = stage.stageId || stage.stageCode
                  const title = getLocalizedField(stage, 'title') || stage.stageCode
                  const isVisible = getStageVisibility(stageId)
                  return (
                    <label key={stageId} className="flex items-center justify-between px-2 py-1 text-xs cursor-pointer hover:bg-gray-50 rounded">
                      <span className="text-gray-700">{title}</span>
                      <Switch checked={isVisible} onCheckedChange={() => toggleStageVisibility(stageId)} disabled={!canEdit} />
                    </label>
                  )
                })}
              </div>
            </div>
            <div className="h-px bg-gray-100" />
          </>
        )}

        {/* Note */}
        <div>
          <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">{t('note') || 'Note'}</Label>
          {canEdit ? (
            <>
              <textarea
                value={note}
                onChange={(e) => { setNote(e.target.value); markDirty() }}
                className="w-full text-xs border border-gray-200 rounded-md p-2 h-16 resize-none focus:outline-none focus:ring-1 focus:ring-blue-300"
                placeholder={t('fieldNotePlaceholder') || 'Add a note...'}
              />
              <label className="flex items-center gap-1.5 text-xs mt-1 cursor-pointer">
                <Checkbox checked={displayNote} disabled={!note?.trim()} onCheckedChange={(c) => { setDisplayNote(!!c); markDirty() }} />
                {t('displayNoteInGrid') || 'Display note on column header'}
              </label>
            </>
          ) : field?.note ? (
            <div className="text-xs text-gray-600 p-2 bg-gray-50 rounded border max-h-16 overflow-y-auto" dangerouslySetInnerHTML={{ __html: field.note }} />
          ) : (
            <p className="text-xs text-gray-400">No note</p>
          )}
        </div>

        <div className="h-px bg-gray-100" />

        {/* Properties toggles */}
        <div className="space-y-3">
          <Label className="text-[10px] text-gray-400 uppercase tracking-wide block">Properties</Label>

          {/* Client Visible */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-amber-500" />
              <span className="text-xs text-gray-700">{t('clientVisibleColumn') || 'Client Visible'}</span>
            </div>
            {canEdit ? (
              <Switch checked={isEnabledForClients} onCheckedChange={(c) => { setIsEnabledForClients(c); markDirty() }} />
            ) : (
              <span className={`text-xs ${isEnabledForClients ? 'text-amber-600' : 'text-gray-400'}`}>{isEnabledForClients ? 'Yes' : 'No'}</span>
            )}
          </div>

          {/* Retain Data */}
          {isCustomField && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare size={14} className="text-blue-500" />
                <span className="text-xs text-gray-700">{t('retainColumnData') || 'Retain Data'}</span>
              </div>
              {canEdit ? (
                <Switch checked={isRetainDataEnabled} onCheckedChange={(c) => { setIsRetainDataEnabled(c); markDirty() }} />
              ) : (
                <span className={`text-xs ${isRetainDataEnabled ? 'text-blue-600' : 'text-gray-400'}`}>{isRetainDataEnabled ? 'Yes' : 'No'}</span>
              )}
            </div>
          )}

          {/* Private Column */}
          {canViewPrivate && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock size={14} className="text-gray-500" />
                  <span className="text-xs text-gray-700">{t('privateColumn') || 'Private Column'}</span>
                </div>
                {canEdit ? (
                  <Switch checked={isPrivate} onCheckedChange={(c) => { setIsPrivate(c); if (!c) setAuthorizedUsers([]); markDirty() }} />
                ) : (
                  <span className={`text-xs ${isPrivate ? 'text-green-600' : 'text-gray-400'}`}>{isPrivate ? 'Yes' : 'No'}</span>
                )}
              </div>

              {/* Authorized Users (when private) */}
              {isPrivate && canEdit && (
                <div className="ml-6 space-y-2">
                  <Label className="text-[10px] text-gray-500">{t('whoCanViewColumn') || 'Authorized Users'}</Label>
                  {authorizedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {authorizedUsers.map((user: any) => (
                        <span key={user.profileId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] border border-blue-200">
                          {user.fullName || user.email || `#${user.profileId}`}
                          <button onClick={() => { setAuthorizedUsers(authorizedUsers.filter((u: any) => u.profileId !== user.profileId)); markDirty() }}>
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Input
                      placeholder={t('searchUsersPlaceholder') || 'Search users...'}
                      className="h-7 text-xs"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                    />
                    {userSearchResults.length > 0 && (
                      <div className="absolute z-10 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                        {userSearchResults.map((user: any) => (
                          <button
                            key={user.profileId}
                            onClick={() => { setAuthorizedUsers([...authorizedUsers, user]); setUserSearchQuery(''); setUserSearchResults([]); markDirty() }}
                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b border-gray-50"
                          >
                            {user.fullName} <span className="text-gray-400">{user.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Custom Values (read-only for non-dropdown or non-editable) */}
        {!isDropdown && field?.customValues?.filter((v: any) => !v.isDeleted).length > 0 && (
          <>
            <div className="h-px bg-gray-100" />
            <div>
              <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">
                {t('dropdownOptions') || 'Options'} ({field?.customValues?.filter((v: any) => !v.isDeleted).length || 0})
              </Label>
              <div className="space-y-0.5 max-h-24 overflow-y-auto">
                {(field?.customValues || []).filter((v: any) => !v.isDeleted).map((val: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded text-xs">
                    {val.color && <span className="w-3 h-3 rounded-full flex-shrink-0 border" style={{ background: val.color }} />}
                    <span className="text-gray-700">{getLocalizedField(val, 'title') || val.code}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer — Save / Cancel buttons */}
      {canEdit && (
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-[#F0EEFF] flex-shrink-0">
          {isDirty && (
            <Button
              variant="elliptical-outline"
              onClick={() => {
                const confirmed = window.confirm('Discard unsaved field changes?')
                if (confirmed) {
                  fieldEditDirtyRef.current = false
                  if (addMode) {
                    dispatch({ type: 'SET_ADDING_CUSTOM_FIELD', payload: false })
                  } else {
                    dispatch({ type: 'SELECT_FIELD', payload: null })
                  }
                }
              }}
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              {t('vmRevert' as any) || 'Revert'}
            </Button>
          )}
          <Button
            variant="elliptical-outline"
            onClick={() => {
              if (isDirty) {
                const confirmed = window.confirm('You have unsaved field changes. Discard them?')
                if (!confirmed) return
                fieldEditDirtyRef.current = false
              }
              if (addMode) {
                dispatch({ type: 'SET_ADDING_CUSTOM_FIELD', payload: false })
              } else {
                dispatch({ type: 'SELECT_FIELD', payload: null })
              }
            }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="elliptical"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          >
            <Save size={14} className="mr-1" />
            {isSaving ? 'Saving...' : addMode ? 'Create Field' : 'Save Field'}
          </Button>
        </div>
      )}

      {/* Color picker for dropdown option swatches */}
      {showColorPicker && activeColorOption !== null && dropdownOptions[activeColorOption] && (
        <ColorPicker
          isOpen={showColorPicker}
          onClose={() => { setShowColorPicker(false); setActiveColorOption(null) }}
          initialColor={dropdownOptions[activeColorOption].color || '#f0f0f0'}
          onColorSelect={(color) => {
            const newOpts = [...dropdownOptions]
            newOpts[activeColorOption] = { ...newOpts[activeColorOption], color }
            setDropdownOptions(newOpts)
            markDirty()
            setShowColorPicker(false)
            setActiveColorOption(null)
          }}
        />
      )}
    </div>
  )
}
